import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Container, Group, SimpleGrid, Skeleton, Stack, Text, Title } from '@mantine/core';
import PageTitle from '@/components/common/PageTitle';
import { Link } from 'react-router-dom';
import { IconBriefcase, IconMessage, IconShoppingCart, IconTag } from '@tabler/icons-react';
import StatTile from '@/components/app/home/StatTile';
import AddEmployeesCard from '@/components/app/home/AddEmployeesCard';
import StartTradingCard from '@/components/app/home/StartTradingCard';
import FirstStepsCard from '@/components/app/home/FirstStepsCard';
import AttentionList from '@/components/app/home/AttentionList';
import NewDealsList from '@/components/app/home/NewDealsList';
import UpcomingDeadlines from '@/components/app/home/UpcomingDeadlines';
import BestOffersList from '@/components/app/home/BestOffersList';
import { useChat } from '@/chat/useChat';
import { useProfile } from '@/hooks/useProfile';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useIsAgent, useIsManager } from '@/hooks/useRoles';
import { useAllDocuments } from '@/hooks/useAllDocuments';
import { useEmployeeInvitations } from '@/hooks/useEmployeeInvitations';
import { useBestOffers } from '@/hooks/useBestOffers';
import { useBuyTree } from '@/hooks/useBuy';
import { useSaleTree } from '@/hooks/useSell';
import { useDealsOverview, useHasCompletedDeals } from '@/hooks/useDealsOverview';
import { useNotificationList } from '@/hooks/useNotifications';
import { flattenBuyTree } from '@/lib/buyTree';
import { flattenSaleTree } from '@/lib/sellTree';
import { buildAttention, countNewDeals, newDealsForList, upcomingDeadlines } from '@/lib/dashboard';
import { ROUTES } from '@/routes/paths';
import { formatOrgName } from '@/lib/format';

const TILE_ICON = { size: 28, color: 'var(--mantine-color-brand-6)' } as const;

// Дашборд кабинета: приветствие + умные плитки (живые счётчики) + лента «требует внимания»
// (мой ход / приостановки) + новые сделки по моим предложениям. Тонкий: расчёты — в lib/dashboard.
export default function AppHome() {
  const { t } = useTranslation();
  const { data: profile } = useProfile();
  const { rooms: chatRooms, unreadTotal: chatUnread } = useChat();
  // Активные чаты — не read-only (по завершённым сделкам).
  const activeChats = chatRooms.filter((room) => room.writable).length;
  const buyTree = useBuyTree();
  const saleTree = useSaleTree();
  const overview = useDealsOverview();
  const hasCompletedDeals = useHasCompletedDeals();
  // «Уже торговал» = есть активные ИЛИ завершённые сделки → напоминание вместо «создайте ПЕРВОЕ».
  const hasTraded = overview.total > 0 || hasCompletedDeals;
  const notifications = useNotificationList();
  const bestOffers = useBestOffers();

  // Онбординг «Добавьте сотрудников»: только владельцу-менеджеру аттестованной организации, у которой
  // ещё нет сотрудников (ни принятых, ни ожидающих приглашений). Запрос не шлём, пока условие не
  // выполнено, — бэкенд иначе ответит 406.
  const isManager = useIsManager();
  const isAgent = useIsAgent();
  const orgAttested = profile?.isVerified === true;
  // Управлять штатом может менеджер аттестованной организации ИЛИ менеджер агентской орг (агенту
  // аттестация не требуется). От этого зависит и запрос приглашений, и карточка «Добавьте сотрудников».
  const canManageEmployees = isManager && (orgAttested || isAgent);
  const invitations = useEmployeeInvitations(canManageEmployees);
  // Признак сельхозтоваропроизводителя — из документов аккредитации; грузим только когда он реально нужен
  // (аттестованная организация, не агент — агент листинги не публикует).
  const startTradingDocs = useAllDocuments(orgAttested && !isAgent);
  const hasEmployees = (invitations.data ?? []).some(
    (invitation) => invitation.status === 'ACCEPTED' || invitation.status === 'PENDING',
  );

  // Онбординг активен → плитки разделов и приветственный подзаголовок скрываем (до аттестации в них нет
  // смысла — торговать нельзя); показываем их после аттестации, когда карточка «Первые шаги» уходит.
  const onboarding = useOnboarding();

  // Карточку «Добавьте сотрудников» можно закрыть крестиком (вдруг никого добавлять не планируют).
  // Решение запоминаем per-user в localStorage, чтобы не всплывала снова. Она и так исчезает после
  // первого добавленного сотрудника (hasEmployees).
  const dismissKey = profile?.id != null ? `data1861-add-employees-dismissed:${profile.id}` : null;
  const [addEmployeesDismissed, setAddEmployeesDismissed] = useState(false);
  useEffect(() => {
    setAddEmployeesDismissed(dismissKey != null && localStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);
  const dismissAddEmployees = (): void => {
    if (dismissKey != null) {
      localStorage.setItem(dismissKey, '1');
    }
    setAddEmployeesDismissed(true);
  };
  const showAddEmployees =
    canManageEmployees && invitations.isSuccess && !hasEmployees && !addEmployeesDismissed;

  const greetingName = profile?.firstName || formatOrgName(profile?.name) || '';
  const buyCount = flattenBuyTree(buyTree.data).length;
  const sellCount = flattenSaleTree(saleTree.data).length;
  // Напоминание «создайте первый листинг» аттестованной организации без нужного листинга: сельхоз-
  // товаропроизводителю — предложение о продаже, остальным — прайс-лист. Агенту не показываем. Ждём
  // загрузки деревьев и признака производителя, чтобы карточка не мигала.
  const startTradingMode: 'sell' | 'buy' | null =
    orgAttested && !isAgent && !onboarding.active && !buyTree.isLoading && !saleTree.isLoading && startTradingDocs.isSuccess
      ? startTradingDocs.data.agriculturalProducer
        ? sellCount === 0
          ? 'sell'
          : null
        : buyCount === 0
          ? 'buy'
          : null
      : null;
  const nowMs = Date.now();
  const attention = buildAttention(overview.deals, notifications.data, isAgent, nowMs);
  const newCount = countNewDeals(overview.deals, nowMs);
  const newDeals = newDealsForList(overview.deals, attention, nowMs);
  const deadlines = upcomingDeadlines(overview.deals, nowMs);

  const attentionLoading = overview.isLoading || notifications.isLoading;

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Stack gap={4}>
          <PageTitle>
            {greetingName ? t('app.home.greeting', { name: greetingName }) : t('app.home.title')}
          </PageTitle>
          {!onboarding.active && <Text c="gray.7">{t('app.home.subtitle')}</Text>}
        </Stack>

        {/* Онбординг: пошаговый гид обязательных данных (самоскрывается, когда неактивен). */}
        <FirstStepsCard />

        {/* Плитки разделов — только когда онбординг завершён (до аттестации торговать нельзя). */}
        {!onboarding.active && (
        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
          {/* Купить/Продать агенту не нужны — он не публикует прайсы/предложения. */}
          {!isAgent && (
            <StatTile
              to={ROUTES.APP_BUY}
              icon={<IconShoppingCart {...TILE_ICON} />}
              label={t('app.nav.buy')}
              value={buyCount}
              unit={t('app.home.unit.prices', { count: buyCount })}
              emptyCta={t('app.home.tile.createPrice')}
              loading={buyTree.isLoading}
            />
          )}
          {!isAgent && (
            <StatTile
              to={ROUTES.APP_SELL}
              icon={<IconTag {...TILE_ICON} />}
              label={t('app.nav.sell')}
              value={sellCount}
              unit={t('app.home.unit.offers', { count: sellCount })}
              emptyCta={t('app.home.tile.createOffer')}
              loading={saleTree.isLoading}
            />
          )}
          <StatTile
            to={ROUTES.APP_DEALS}
            icon={<IconBriefcase {...TILE_ICON} />}
            label={t('app.nav.deals')}
            value={overview.isError || overview.partial ? undefined : overview.total}
            unit={t('app.home.unit.deals', { count: overview.total })}
            badge={newCount > 0 ? t('app.home.newBadge', { count: newCount }) : undefined}
            hint={overview.isError || overview.partial ? t('app.home.dealsError') : undefined}
            loading={overview.isLoading}
          />
          <StatTile
            to={ROUTES.APP_CHAT}
            icon={<IconMessage {...TILE_ICON} />}
            label={t('app.nav.chat')}
            value={activeChats}
            unit={t('app.home.unit.chats', { count: activeChats })}
            badge={chatUnread > 0 ? t('app.home.newBadge', { count: chatUnread }) : undefined}
          />
        </SimpleGrid>
        )}

        {startTradingMode ? <StartTradingCard mode={startTradingMode} returning={hasTraded} /> : null}

        {showAddEmployees ? <AddEmployeesCard onClose={dismissAddEmployees} /> : null}

        {attentionLoading ? (
          <Stack gap="sm">
            <Title order={4}>{t('app.home.attention.title')}</Title>
            <Skeleton h={120} />
          </Stack>
        ) : attention.length > 0 ? (
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Title order={4}>{t('app.home.attention.title')}</Title>
              <Anchor component={Link} to={ROUTES.APP_DEALS} size="sm">
                {t('app.home.attention.all')}
              </Anchor>
            </Group>
            <AttentionList items={attention} />
          </Stack>
        ) : overview.total > 0 ? (
          <Stack gap="sm">
            <Title order={4}>{t('app.home.attention.title')}</Title>
            <Text c="gray.6" size="sm">
              {t('app.home.attention.empty')}
            </Text>
          </Stack>
        ) : null}

        {deadlines.length > 0 ? (
          <Stack gap="sm">
            <Title order={4}>{t('app.home.deadlines.title')}</Title>
            <UpcomingDeadlines items={deadlines} />
          </Stack>
        ) : null}

        {bestOffers.data && bestOffers.data.length > 0 ? (
          <Stack gap="sm">
            <Title order={4}>{t('app.home.bestOffers.title')}</Title>
            <BestOffersList items={bestOffers.data} />
          </Stack>
        ) : null}

        {newDeals.length > 0 ? (
          <Stack gap="sm">
            <Title order={4}>{t('app.home.newDeals.title')}</Title>
            <NewDealsList deals={newDeals} />
          </Stack>
        ) : null}
      </Stack>
    </Container>
  );
}
